import { useState, useEffect } from 'react';
import {
  ClipboardCheck,
  Plus,
  Check,
  AlertTriangle,
  Filter,
  Trash2,
  Edit2,
  X,
  Save,
  Droplets,
  Utensils,
  Heart,
  Home,
  Radio,
  Flashlight,
  Wrench,
  FileText,
  Sparkles,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useApp } from '../context/AppContext';
import {
  getAllChecklistItems,
  addChecklistItem,
  updateChecklistItem,
  deleteChecklistItem,
  seedDefaultChecklist,
} from '../db/database';
import type { ChecklistItem } from '../types';

const categoryIcons: Record<string, typeof ClipboardCheck> = {
  'Water': Droplets,
  'Food': Utensils,
  'Medical': Heart,
  'Shelter': Home,
  'Communication': Radio,
  'Light & Power': Flashlight,
  'Tools': Wrench,
  'Documents': FileText,
  'Hygiene': Sparkles,
};

export function Checklist() {
  const { addNotification } = useApp();
  const [items, setItems] = useState<ChecklistItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState<ChecklistItem | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    category: 'Other',
    quantity: 0,
    quantityNeeded: 1,
    unit: 'units',
    priority: 'important' as ChecklistItem['priority'],
    notes: '',
  });

  useEffect(() => {
    loadItems();
  }, []);

  async function loadItems() {
    try {
      await seedDefaultChecklist();
      const data = await getAllChecklistItems();
      setItems(data);
    } catch (error) {
      console.error('Failed to load checklist:', error);
    } finally {
      setLoading(false);
    }
  }

  function resetForm() {
    setFormData({
      name: '',
      category: 'Other',
      quantity: 0,
      quantityNeeded: 1,
      unit: 'units',
      priority: 'important',
      notes: '',
    });
    setEditingItem(null);
  }

  function openAddModal() {
    resetForm();
    setShowModal(true);
  }

  function openEditModal(item: ChecklistItem) {
    setEditingItem(item);
    setFormData({
      name: item.name,
      category: item.category,
      quantity: item.quantity,
      quantityNeeded: item.quantityNeeded,
      unit: item.unit,
      priority: item.priority,
      notes: item.notes || '',
    });
    setShowModal(true);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();

    try {
      if (editingItem) {
        await updateChecklistItem(editingItem.id, { ...formData, isChecked: editingItem.isChecked });
        addNotification('success', 'Updated', 'Item updated successfully');
      } else {
        await addChecklistItem({ ...formData, isChecked: false });
        addNotification('success', 'Added', 'Item added to checklist');
      }
      setShowModal(false);
      resetForm();
      loadItems();
    } catch (error) {
      console.error('Failed to save item:', error);
      addNotification('error', 'Error', 'Failed to save item');
    }
  }

  async function handleToggleCheck(item: ChecklistItem) {
    const newChecked = !item.isChecked;
    await updateChecklistItem(item.id, {
      isChecked: newChecked,
      lastCheckedAt: newChecked ? Date.now() : undefined,
    });
    loadItems();
  }

  async function handleQuantityChange(item: ChecklistItem, delta: number) {
    const newQuantity = Math.max(0, item.quantity + delta);
    await updateChecklistItem(item.id, { quantity: newQuantity });
    loadItems();
  }

  async function handleDelete(id: string) {
    if (!confirm('Remove this item from checklist?')) return;
    await deleteChecklistItem(id);
    loadItems();
  }

  const categories = [...new Set(items.map((item) => item.category))];

  const filteredItems = categoryFilter === 'all'
    ? items
    : items.filter((item) => item.category === categoryFilter);

  const groupedItems = filteredItems.reduce((acc, item) => {
    if (!acc[item.category]) {
      acc[item.category] = [];
    }
    acc[item.category].push(item);
    return acc;
  }, {} as Record<string, ChecklistItem[]>);

  const stats = {
    total: items.length,
    checked: items.filter((i) => i.isChecked).length,
    essential: items.filter((i) => i.priority === 'essential').length,
    essentialChecked: items.filter((i) => i.priority === 'essential' && i.isChecked).length,
  };

  const overallProgress = stats.total > 0 ? Math.round((stats.checked / stats.total) * 100) : 0;
  const essentialProgress = stats.essential > 0 ? Math.round((stats.essentialChecked / stats.essential) * 100) : 0;

  const getProgressColor = (progress: number) => {
    if (progress >= 80) return 'bg-safe-500';
    if (progress >= 50) return 'bg-warning-500';
    return 'bg-emergency-500';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <ClipboardCheck className="w-6 h-6 text-emergency-400" />
            Emergency Checklist
          </h1>
          <p className="text-gray-400 mt-1">Track your emergency supplies and preparedness</p>
        </div>
        <button onClick={openAddModal} className="btn-emergency">
          <Plus className="w-4 h-4" />
          Add Item
        </button>
      </div>

      {/* Progress Cards */}
      <div className="grid md:grid-cols-2 gap-4">
        <div className="card p-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-white">Overall Progress</h3>
            <span className={`text-2xl font-bold ${
              overallProgress >= 80 ? 'text-safe-400' :
              overallProgress >= 50 ? 'text-warning-400' : 'text-emergency-400'
            }`}>
              {overallProgress}%
            </span>
          </div>
          <div className="h-3 bg-gray-700 rounded-full overflow-hidden">
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${overallProgress}%` }}
              transition={{ duration: 0.5 }}
              className={`h-full ${getProgressColor(overallProgress)}`}
            />
          </div>
          <p className="text-sm text-gray-400 mt-2">
            {stats.checked} of {stats.total} items ready
          </p>
        </div>

        <div className="card p-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-white flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-emergency-400" />
              Essential Items
            </h3>
            <span className={`text-2xl font-bold ${
              essentialProgress >= 80 ? 'text-safe-400' :
              essentialProgress >= 50 ? 'text-warning-400' : 'text-emergency-400'
            }`}>
              {essentialProgress}%
            </span>
          </div>
          <div className="h-3 bg-gray-700 rounded-full overflow-hidden">
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${essentialProgress}%` }}
              transition={{ duration: 0.5 }}
              className={`h-full ${getProgressColor(essentialProgress)}`}
            />
          </div>
          <p className="text-sm text-gray-400 mt-2">
            {stats.essentialChecked} of {stats.essential} essential items ready
          </p>
        </div>
      </div>

      {/* Category Filter */}
      <div className="flex items-center gap-2 overflow-x-auto pb-2">
        <Filter className="w-4 h-4 text-gray-500 flex-shrink-0" />
        <button
          onClick={() => setCategoryFilter('all')}
          className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${
            categoryFilter === 'all'
              ? 'bg-emergency-600 text-white'
              : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
          }`}
        >
          All Categories
        </button>
        {categories.map((cat) => (
          <button
            key={cat}
            onClick={() => setCategoryFilter(cat)}
            className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${
              categoryFilter === cat
                ? 'bg-emergency-600 text-white'
                : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
            }`}
          >
            {cat}
          </button>
        ))}
      </div>

      {/* Checklist Items */}
      {loading ? (
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="card p-4 animate-pulse">
              <div className="h-4 bg-gray-700 rounded w-1/4 mb-3" />
              <div className="space-y-2">
                {[1, 2, 3].map((j) => (
                  <div key={j} className="h-12 bg-gray-700/50 rounded" />
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="space-y-6">
          {Object.entries(groupedItems).map(([category, categoryItems]) => {
            const CategoryIcon = categoryIcons[category] || ClipboardCheck;
            const categoryProgress = Math.round(
              (categoryItems.filter((i) => i.isChecked).length / categoryItems.length) * 100
            );

            return (
              <motion.div
                key={category}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="card overflow-hidden"
              >
                <div className="p-4 border-b border-gray-700 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-gray-700 rounded-lg">
                      <CategoryIcon className="w-5 h-5 text-emergency-400" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-white">{category}</h3>
                      <p className="text-xs text-gray-400">
                        {categoryItems.filter((i) => i.isChecked).length} / {categoryItems.length} items
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-24 h-2 bg-gray-700 rounded-full overflow-hidden">
                      <div
                        className={`h-full ${getProgressColor(categoryProgress)}`}
                        style={{ width: `${categoryProgress}%` }}
                      />
                    </div>
                    <span className="text-sm text-gray-400">{categoryProgress}%</span>
                  </div>
                </div>

                <div className="divide-y divide-gray-700/50">
                  {categoryItems.map((item) => (
                    <div
                      key={item.id}
                      className={`p-4 flex items-center gap-4 transition-colors ${
                        item.isChecked ? 'bg-safe-900/10' : ''
                      }`}
                    >
                      <button
                        onClick={() => handleToggleCheck(item)}
                        className={`w-6 h-6 rounded-md border-2 flex items-center justify-center transition-all ${
                          item.isChecked
                            ? 'bg-safe-500 border-safe-500 text-white'
                            : 'border-gray-500 hover:border-safe-500'
                        }`}
                      >
                        {item.isChecked && <Check className="w-4 h-4" />}
                      </button>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`font-medium ${item.isChecked ? 'text-gray-400 line-through' : 'text-white'}`}>
                            {item.name}
                          </span>
                          {item.priority === 'essential' && (
                            <span className="badge-emergency text-xs">Essential</span>
                          )}
                          {item.priority === 'optional' && (
                            <span className="badge bg-gray-700 text-gray-400 text-xs">Optional</span>
                          )}
                        </div>
                        <div className="flex items-center gap-3 text-sm">
                          <span className={
                            item.quantity >= item.quantityNeeded
                              ? 'text-safe-400'
                              : item.quantity > 0
                              ? 'text-warning-400'
                              : 'text-emergency-400'
                          }>
                            {item.quantity} / {item.quantityNeeded} {item.unit}
                          </span>
                        </div>
                      </div>

                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => handleQuantityChange(item, -1)}
                          disabled={item.quantity <= 0}
                          className="w-8 h-8 rounded bg-gray-700 text-gray-300 hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                        >
                          -
                        </button>
                        <span className="w-10 text-center text-white font-medium">{item.quantity}</span>
                        <button
                          onClick={() => handleQuantityChange(item, 1)}
                          className="w-8 h-8 rounded bg-gray-700 text-gray-300 hover:bg-gray-600 flex items-center justify-center"
                        >
                          +
                        </button>
                      </div>

                      <div className="flex gap-1">
                        <button
                          onClick={() => openEditModal(item)}
                          className="p-2 rounded-lg hover:bg-gray-700 text-gray-400 hover:text-white transition-colors"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(item.id)}
                          className="p-2 rounded-lg hover:bg-emergency-900/50 text-gray-400 hover:text-emergency-400 transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </motion.div>
            );
          })}
        </div>
      )}

      {/* Add/Edit Modal */}
      <AnimatePresence>
        {showModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setShowModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-gray-800 rounded-xl border border-gray-700 w-full max-w-md max-h-[90vh] overflow-y-auto"
            >
              <div className="p-4 border-b border-gray-700 flex items-center justify-between">
                <h2 className="text-lg font-semibold text-white">
                  {editingItem ? 'Edit Item' : 'Add Item'}
                </h2>
                <button
                  onClick={() => setShowModal(false)}
                  className="p-2 rounded-lg hover:bg-gray-700 text-gray-400"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="p-4 space-y-4">
                <div>
                  <label className="label">Item Name *</label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="input"
                    placeholder="Water bottles, First aid kit..."
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="label">Category</label>
                    <select
                      value={formData.category}
                      onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                      className="input"
                    >
                      {['Water', 'Food', 'Medical', 'Shelter', 'Communication', 'Light & Power', 'Tools', 'Documents', 'Hygiene', 'Other'].map((cat) => (
                        <option key={cat} value={cat}>{cat}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="label">Priority</label>
                    <select
                      value={formData.priority}
                      onChange={(e) => setFormData({ ...formData, priority: e.target.value as ChecklistItem['priority'] })}
                      className="input"
                    >
                      <option value="essential">Essential</option>
                      <option value="important">Important</option>
                      <option value="optional">Optional</option>
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="label">Have</label>
                    <input
                      type="number"
                      min="0"
                      value={formData.quantity}
                      onChange={(e) => setFormData({ ...formData, quantity: parseInt(e.target.value) || 0 })}
                      className="input"
                    />
                  </div>
                  <div>
                    <label className="label">Need</label>
                    <input
                      type="number"
                      min="1"
                      value={formData.quantityNeeded}
                      onChange={(e) => setFormData({ ...formData, quantityNeeded: parseInt(e.target.value) || 1 })}
                      className="input"
                    />
                  </div>
                  <div>
                    <label className="label">Unit</label>
                    <input
                      type="text"
                      value={formData.unit}
                      onChange={(e) => setFormData({ ...formData, unit: e.target.value })}
                      className="input"
                      placeholder="units"
                    />
                  </div>
                </div>

                <div>
                  <label className="label">Notes</label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    className="input min-h-[80px] resize-none"
                    placeholder="Additional notes..."
                  />
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="btn-secondary flex-1"
                  >
                    Cancel
                  </button>
                  <button type="submit" className="btn-emergency flex-1">
                    <Save className="w-4 h-4" />
                    {editingItem ? 'Update' : 'Save'}
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
